import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useKpiTrend } from "@/hooks/useKpiTrend";
import { CeremonyTaxGauge } from "./CeremonyTaxGauge";
import { PeriodFilter } from "./PeriodFilter";
import { EfficiencyTrendChart } from "./EfficiencyTrendChart";
import { Loader2 } from "lucide-react";

/** Map UI period (days) to API parameter (weeks). */
export function periodToWeeks(days: number): number {
  if (days === 0) return 52; // All time ~1 year
  return Math.max(1, Math.ceil(days / 7));
}

interface TokenEconomyPanelProps {
  projectSlug: string | null;
  /** When provided, the panel uses this period and hides its internal PeriodFilter. */
  externalPeriodDays?: number;
}

export function TokenEconomyPanel({
  projectSlug,
  externalPeriodDays,
}: TokenEconomyPanelProps) {
  const [internalPeriodDays, setInternalPeriodDays] = useState(30);
  const periodDays = externalPeriodDays ?? internalPeriodDays;
  const { data, loading, error } = useKpiTrend(periodToWeeks(periodDays), projectSlug);

  if (loading && !data) {
    return (
      <div className="flex h-[300px] items-center justify-center rounded-lg border text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading efficiency data...
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex h-[300px] items-center justify-center rounded-lg border">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (!data || data.weeks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Cost Efficiency Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <EfficiencyTrendChart data={{ weeks: [], trend: "stable", overhead_ratio: 0, outliers: [], current_avg: null }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-semibold">Cost Efficiency Trend</CardTitle>
        {externalPeriodDays === undefined && (
          <PeriodFilter value={periodDays} onChange={setInternalPeriodDays} />
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <EfficiencyTrendChart data={data} />
          </div>
          <CeremonyTaxGauge ratio={data.overhead_ratio} />
        </div>
      </CardContent>
    </Card>
  );
}
